import type { ReactNode } from "react";
import { CHEVRON_ACTION_CLASS } from "./ChevronAction";
import { ChevronRightIcon } from "./icons";
import { SessionNumber } from "./SessionNumber";

// CoupleSessionRow — a single row in a couple's session list (Upcoming standing-slot
// list, Past-sessions timeline). A numbered badge + a stacked date/time, with an
// optional trailing action (e.g. "Reschedule", "Share feedback") carrying a
// chevron. Presentation-only; the caller owns the action.
//
// Text-and-data (DESIGN.md §1): the badge is the session number on the brand
// sage tint — no photos or icons beyond the affordance chevron. The number is
// decorative (the date carries the accessible meaning), so it's aria-hidden.

export interface CoupleSessionRowAction {
    label: string;
    onClick: () => void;
    /** Accessible label for the row, which is the button when an action is set (defaults to `label`). */
    ariaLabel?: string;
}

export interface CoupleSessionRowProps {
    /** Session number shown in the leading badge. */
    number: number;
    /** Primary line, e.g. "Thu, 18 Jun". */
    date: string;
    /** Optional secondary line, e.g. "4:00pm". */
    time?: string;
    /** Optional extra meta rendered after the date on the same line (e.g. a status). */
    meta?: ReactNode;
    /** Optional trailing action (Reschedule / Share feedback). */
    action?: CoupleSessionRowAction;
    className?: string;
}


export function CoupleSessionRow({ number, date, time, meta, action, className = "" }: CoupleSessionRowProps) {
    const body = (
        <>
            <SessionNumber n={number} />
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="[font:var(--type-body)] font-medium text-primary">
                    {date}
                    {meta != null ? <span className="text-secondary font-normal"> · {meta}</span> : null}
                </span>
                {time ? <span className="[font:var(--type-caption)] text-secondary">{time}</span> : null}
            </span>
        </>
    );

    if (!action) {
        return <div className={["flex items-center gap-4 py-4", className].filter(Boolean).join(" ")}>{body}</div>;
    }

    // With an action, the WHOLE ROW is the button: a phone-sized tap target instead of the
    // small label alone. The trailing label keeps the ChevronAction look as a plain span
    // (a nested <button> would be invalid) and lights up on `group-hover` anywhere on the row.
    return (
        <button
            type="button"
            onClick={action.onClick}
            // The row's own text would otherwise be replaced by the action alone, so the name
            // carries both: "Reschedule session 7, Thu, Oct 1 8:03 AM".
            aria-label={`${action.ariaLabel ?? action.label}, ${date}${time ? ` ${time}` : ""}`}
            className={[
                // Bleeds through the card's px-4 gutter so the hover fill spans the card edge to
                // edge (the list container clips it with overflow-hidden); --canvas on hover, the
                // ground token, reads as the row pressing into the card.
                "group -mx-4 flex w-[calc(100%+2rem)] cursor-pointer items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-interactive",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {body}
            <span aria-hidden="true" className={CHEVRON_ACTION_CLASS}>
                {action.label}
                <ChevronRightIcon />
            </span>
        </button>
    );
}
